<template>
  <FiltersMenu
    offset-y
    bottom
    right
    :max-height="480"
    :nudge-bottom="16"
    transition="slide-y-transition"
  >
    <TableFieldFilter
      label="Divisions"
      :all-items="divisionOptions"
      :selected-items="selectedDivisions"
      @selected="setSelectedDivisions"
    />

    <TableFieldFilter
      label="Resource Types"
      :all-items="resourceTypeOptions"
      :selected-items="selectedResourceTypes"
      @selected="setSelectedResourceTypes"
    />

    <div class="subheading mb-2">Scheduled filters</div>

    <TableFieldFilter
      label="Service Providers"
      :all-items="serviceProviderOptions"
      :selected-items="selectedServiceProviders"
      @selected="setSelectedServiceProviders"
    />

    <template #post-filters>
      <vs-wrapper space="small">
        <v-row dense align="center">
          <v-col><vs-text>Show offline resources</vs-text></v-col>
          <v-col class="shrink">
            <v-switch v-model="showOfflineUnits" class="ma-0" hide-details />
          </v-col>
        </v-row>
        <v-row dense align="center">
          <v-col><vs-text>Show standby resources</vs-text></v-col>
          <v-col class="shrink">
            <v-switch v-model="showStandbyUnits" class="ma-0" hide-details />
          </v-col>
        </v-row>
        <v-row dense align="center">
          <v-col><vs-text>Show resources with no assigned jobs</vs-text></v-col>
          <v-col class="shrink">
            <v-switch v-model="showUnitsWithNoAssignedJobs" class="ma-0" hide-details />
          </v-col>
        </v-row>
      </vs-wrapper>
    </template>
  </FiltersMenu>
</template>

<script setup lang="ts">
import FiltersMenu from '../common/FiltersMenu.vue'
import TableFieldFilter from '../common/TableFieldFilter.vue'
import { useFilteredJobStoreLegacyAdapter as useJobFilterStore } from '../jobs/job-search-page/filteredJobStore'
import { computed, onMounted, watch } from 'vue'
import { useGetJobFilterOptions } from '@/api/filter-options'
import { Dictionary, groupBy, orderBy, sortBy } from 'lodash'
import { useScheduleFilterStore } from './ScheduleFilterStore'
import { until } from '@vueuse/core'

// Store
const scheduleViewStore = useScheduleFilterStore()
const store = useJobFilterStore()
const { data: filterOptions, isFetched: isFetchedFilterOptions } = useGetJobFilterOptions()

onMounted(async () => {
  await until(isFetchedFilterOptions).toBe(true)

  scheduleViewStore.updateDivisionFiltersExplicitlySet(store.selectedDivisions.value.length > 0)
  scheduleViewStore.updateResourceTypeFiltersExplicitlySet(
    store.selectedResourceTypes.value.length > 0,
  )
  scheduleViewStore.updateServiceProviderFiltersExplicitlySet(
    store.selectedServiceProviders.value.length > 0,
  )
})

// Computed
const resetFiltersState = computed(() => scheduleViewStore.resetFilters)

const divisionOptions = computed(() => {
  const grouped = groupBy(filterOptions.value?.jobFilterOptions.divisionOptions, (d) => d.hubName)

  return withHeaders(grouped)
})

const resourceTypeOptions = computed(() => {
  const grouped = groupBy(
    filterOptions.value?.jobFilterOptions.resourceTypeOptions,
    (d) => d.serviceName,
  )

  return withHeaders(grouped)
})

const serviceProviderOptions = computed(() => {
  return [
    { id: null, name: 'Unassigned' },
    ...(filterOptions?.value?.jobFilterOptions.serviceProviderOptions ?? []),
  ]
})

const selectedDivisions = computed<string[]>(() => {
  if (
    !scheduleViewStore.divisionFiltersExplicitlySet &&
    store.selectedDivisions.value.length === 0
  ) {
    return filterOptions.value?.jobFilterOptions.divisionOptions.map((d) => d.id) ?? []
  }

  return store.selectedDivisions.value
})

const selectedResourceTypes = computed(() => {
  if (
    !scheduleViewStore.resourceTypeFiltersExplicitlySet &&
    store.selectedResourceTypes.value.length === 0
  ) {
    return filterOptions.value?.jobFilterOptions.resourceTypeOptions.map((d) => d.id) ?? []
  }

  return store.selectedResourceTypes.value
})

const selectedServiceProviders = computed<(string | null)[]>(() => {
  if (
    !scheduleViewStore.serviceProviderFiltersExplicitlySet &&
    store.selectedServiceProviders.value.length === 0
  ) {
    return [
      null,
      ...(filterOptions.value?.jobFilterOptions.serviceProviderOptions.map((d) => d.id) ?? []),
    ]
  }

  return store.selectedServiceProviders.value
})

const showOfflineUnits = computed({
  get() {
    return scheduleViewStore.showOfflineUnits
  },
  set(value) {
    scheduleViewStore.updateShowOfflineUnits(value!)
  },
})

const showStandbyUnits = computed({
  get() {
    return scheduleViewStore.showStandbyUnits
  },
  set(value) {
    scheduleViewStore.updateShowStandbyUnits(value!)
  },
})

const showUnitsWithNoAssignedJobs = computed({
  get() {
    return scheduleViewStore.showUnitsWithNoAssignedJobs
  },
  set(value) {
    scheduleViewStore.updateShowUnitsWithNoAssignedJobs(value!)
  },
})

// Watchers
watch(resetFiltersState, () => {
  clearSelectedDivisions()
  clearSelectedServiceProviders()
  clearSelectedResourceTypes()

  scheduleViewStore.updateFilterReset(false)
})

// Methods
const setSelectedDivisions = ({ ids, hidden }: { ids: string[]; hidden: boolean }) => {
  const options = filterOptions.value?.jobFilterOptions.divisionOptions
  const selectedDivisionsIds = options?.filter((d) => ids.includes(d.id))
  const selectedDivisions = selectedDivisionsIds?.map((d) => {
    return {
      label: d.name,
      value: d.id,
    }
  })

  scheduleViewStore.divisionFiltersExplicitlySet = true

  store.$patch(
    {
      selectedDivisions,
    },
    hidden,
  )
}

const setSelectedResourceTypes = ({ ids, hidden }: { ids: number[]; hidden: boolean }) => {
  const options = filterOptions.value?.jobFilterOptions.resourceTypeOptions
  const selectedResourceTypesIds = options?.filter((rt) => ids.includes(rt.id))
  const selectedResourceTypes = selectedResourceTypesIds?.map((rt) => {
    return {
      label: rt.name,
      value: rt.id,
    }
  })

  scheduleViewStore.resourceTypeFiltersExplicitlySet = true
  store.$patch(
    {
      selectedResourceTypes,
    },
    hidden,
  )
}

const setSelectedServiceProviders = ({
  ids,
  hidden,
}: {
  ids: (string | null)[]
  hidden: boolean
}) => {
  const options = filterOptions.value?.jobFilterOptions.serviceProviderOptions
  const hasNull = ids.includes(null)
  // filter out null if it exists
  const filteredIds = hasNull ? ids.filter((id) => id !== null) : ids
  const selectedServiceProviders = options?.filter((sp) => filteredIds.includes(sp.id))
  const selectedProviders = selectedServiceProviders?.map((sp) => {
    return {
      label: sp.name,
      value: sp.id,
    }
  })

  if (hasNull) {
    selectedProviders?.push({
      label: 'Unassigned',
      value: 'null',
    })
  }

  scheduleViewStore.updateServiceProviderFiltersExplicitlySet(true)
  store.$patch(
    {
      selectedServiceProviders: selectedProviders,
    },
    hidden,
  )
}

const clearSelectedDivisions = () => {
  store.$patch({
    selectedDivisions: [],
  })
  scheduleViewStore.updateDivisionFiltersExplicitlySet(false)
}

const clearSelectedResourceTypes = () => {
  store.$patch({
    selectedResourceTypes: [],
  })
  scheduleViewStore.updateResourceTypeFiltersExplicitlySet(false)
}

const clearSelectedServiceProviders = () => {
  store.$patch({
    selectedServiceProviders: [],
  })
  scheduleViewStore.updateServiceProviderFiltersExplicitlySet(false)
}

const withHeaders = function <T>(grouped: Dictionary<T[]>) {
  return orderBy(Object.keys(grouped))
    .map((groupKey) => [{ header: groupKey }, ...sortBy(grouped[groupKey])])
    .reduce((aggregate, items) => [...aggregate, ...items], [])
}
</script>
